import React, { useEffect, useContext, useState, useRef } from 'react';
import FadeIn from 'react-fade-in';
import { GlobalHotKeys } from 'react-hotkeys';
import { useObserver } from 'mobx-react-lite';

import QuickDrawerHeader from '../../../../_shared/QuickDrawerHeader';
import { quickDrawerFocus } from '../../../../_shared/QuickDrawer';

import ExamInputMultipleUpdateStore from '../../../../../../stores/ExamInputMultipleUpdateStore';
import QuickDrawerStore from '../../../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../../../constants/errorMessages';
import * as fn from '../../../../../../utilities/_functions';
import * as oh from '../../../../../../utilities/operationHelper';
import * as tih from '../../../../../../utilities/templateInputHelper';

import './UpdateExamInputMultiple.scss';

function UpdateExamInputMultiple(props) {
    const isMounted = useRef(true);
    const focusTimer = useRef(null);
    const examInputs = useContext(ExamInputMultipleUpdateStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [size, setSize] = useState('');
    const [align, setAlign] = useState('');
    const [valign, setValign] = useState('');
    const [inline, setInline] = useState(false);
    const [dataType, setDataType] = useState(null);
    const [paddingType, setPaddingType] = useState(null);
    const [paddingCharacter, setPaddingCharacter] = useState(null);
    const [paddingLength, setPaddingLength] = useState(null);
    const [decimalPlaces, setDecimalPlaces] = useState(null);
    const [isDefaultNegative, setIsDefaultNegative] = useState(null);
    const [format, setFormat] = useState(null);
    const [formatSearch, setFormatSearch] = useState(null);
    const [formatReplace, setFormatReplace] = useState(null);
    const [validation, setValidation] = useState(null);
    const [step, setStep] = useState(null);
    const [historyTooltipEnabled, setHistoryTooltipEnabled] = useState(false);

    useEffect(() => {
        focusTimer.current = setTimeout(() => {
            setSize(examInputs.getMetadata('size'));
            setAlign(examInputs.getMetadata('align'));
            setValign(examInputs.getMetadata('valign'));
            setInline(examInputs.getMetadata('inline'));
            setDataType(examInputs.getMetadata('dataType'));
            setPaddingType(examInputs.getMetadata('paddingType'));
            setPaddingCharacter(examInputs.getMetadata('paddingCharacter'));
            setPaddingLength(examInputs.getMetadata('paddingLength'));
            setIsDefaultNegative(examInputs.getMetadata('isDefaultNegative'));
            setDecimalPlaces(examInputs.getMetadata('decimalPlaces'));
            setFormat(examInputs.getMetadata('format'));
            setFormatSearch(examInputs.getMetadata('formatSearch'));
            setFormatReplace(examInputs.getMetadata('formatReplace'));
            setValidation(examInputs.getMetadata('validation'));
            setStep(examInputs.getMetadata('step'));
            setHistoryTooltipEnabled(examInputs.getMetadata('historyTooltipEnabled'));
            quickDrawerFocus(props.drawer);
        }, 100)

        return (() => {
            isMounted.current = false;
            examInputs.clear();
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        })
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (examInputs.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = event => {
        const updated = examInputs.hasUnsavedChanges;

        event.preventDefault();
        handlePopulateMetadata();

        examInputs.save()
            .then(data => {
                if (isMounted.current) {
                    if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                        props.onSuccess(event, { updated: updated, data: data });
                    }
                }
            })
    }

    const handleSizeChange = event => {
        setSize(event.target.value);
        examInputs.hasUnsavedChanges = true;
    }

    const handleAlignChange = value => {
        setAlign((value ? value : 'left'));
        examInputs.hasUnsavedChanges = true;
    }

    const handleValignChange = value => {
        setValign((value ? value : 'top'));
        examInputs.hasUnsavedChanges = true;
    }

    const handleInlineChange = event => {
        setInline((event.target.value === 'true'));
        examInputs.hasUnsavedChanges = true;
    }

    const handlePopulateMetadata = () => {
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_SIZE, 'size', size);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_ALIGN, 'align', align);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_VALIGN, 'valign', valign);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_INLINE, 'inline', inline);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_TYPE, 'dataType', dataType);
        if (!!paddingType && !!paddingCharacter && !!paddingLength) {
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_TYPE, 'paddingType', paddingType);
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_CHARACTER, 'paddingCharacter', paddingCharacter);
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_LENGTH, 'paddingLength', paddingLength);
        }
        else {
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_TYPE, 'paddingType', null);
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_CHARACTER, 'paddingCharacter', null);
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_LENGTH, 'paddingLength', null);
        }
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_DEFAULT_NEGATIVE, 'isDefaultNegative', isDefaultNegative);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_DECIMAL_PLACES, 'decimalPlaces', decimalPlaces);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT, 'format', format);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT_SEARCH, 'formatSearch', formatSearch);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT_REPLACE, 'formatReplace', formatReplace);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_VALIDATION, 'validation', validation);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_STEP, 'step', step);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_HISTORY_TOOLTIP, 'historyTooltipEnabled', historyTooltipEnabled);
    }

    const handleDataTypeChange = event => {
        const value = event.target.value;

        switch (value) {
            case 'String':
                setIsDefaultNegative(null);
                setDecimalPlaces(null);
                setFormat(null);
                setValidation(null);
                break;

            case 'Integer':
                setIsDefaultNegative(false);
                setDecimalPlaces(null);
                setFormat(null);
                setValidation(null);
                clearPadding();
                break;

            case 'Decimal':
                setIsDefaultNegative(false);
                setDecimalPlaces(2);
                setFormat(null);
                setValidation(null);
                clearPadding();
                break;

            default:
                setIsDefaultNegative(null);
                setDecimalPlaces(null);
                setFormat(null);
                setValidation(null);
                clearPadding();
                break;
        }

        setDataType(value);
        examInputs.hasUnsavedChanges = true;
    }

    const handlePaddingTypeChange = event => {
        const value = event.target.value;
        setPaddingType(value);
        examInputs.hasUnsavedChanges = true;

        if (!value) {
            setPaddingCharacter(null);
            setPaddingLength(null);
        }
    }

    const handlePaddingCharacterChange = event => {
        const value = event.target.value;
        setPaddingCharacter(value);
        examInputs.hasUnsavedChanges = true;

        if (!paddingLength) {
            if (value && value.length > 0) {
                setPaddingLength(2)
            }
        }
    }

    const handlePaddingBlur = event => {
        if (paddingCharacter && !paddingLength) {
            setPaddingLength(2)
        }
        if (!paddingCharacter && paddingLength) {
            setPaddingLength(null)
        }
    }

    const handlePaddingLengthChange = event => {
        const value = parseInt(event.target.value, 10);
        setPaddingLength(value);
        examInputs.hasUnsavedChanges = true;
    }

    const handleDefaultNegativeChange = event => {
        const value = !isDefaultNegative;
        setIsDefaultNegative(value);
        examInputs.hasUnsavedChanges = true;
    }

    const handleDecimalPlacesChange = event => {
        const value = parseInt(event.target.value, 10);
        setDecimalPlaces(value);
        examInputs.hasUnsavedChanges = true;
    }

    const handleFormatChange = event => {
        const value = event.target.value;
        setFormat(value);
        examInputs.hasUnsavedChanges = true;
    }

    const handleFormatSearchChange = event => {
        const value = event.target.value;
        setFormatSearch(value);
        examInputs.hasUnsavedChanges = true;
    }

    const handleFormatReplaceChange = event => {
        const value = event.target.value;
        setFormatReplace(value);
        examInputs.hasUnsavedChanges = true;
    }

    const handleValidationChange = event => {
        const value = event.target.value;
        setValidation(value);
        examInputs.hasUnsavedChanges = true;
    }

    const handleStepChange = event => {
        const value = event.target.value;
        const number = !fn.isNullOrUndefined(value) ? Number(value) : null;
        const isDecimal = !isNaN(number) && !Number.isInteger(number);
        const isInt = !isNaN(number) && Number.isInteger(number);

        if (isDecimal || isInt) {
            setStep(value);
        }
        else {
            setStep(null);
        }

        examInputs.hasUnsavedChanges = true;
    }

    const handleHistoryTooltipEnabledChange = event => {
        const value = !historyTooltipEnabled;
        setHistoryTooltipEnabled(value);
        examInputs.hasUnsavedChanges = true;
    }

    const clearPadding = () => {
        setPaddingType(null);
        setPaddingCharacter(null);
        setPaddingLength(null);
    }

    const populateMetadata = (field, key, value) => {
        if (tih.hasField(examInputs.type, field)) {
            examInputs.setMetadata(key, value);
        } else {
            examInputs.removeMetadata(key);
        }
    }

    return useObserver(() => <>
        <>
            {
                (props.drawer === quickDrawer.drawerOpened) ?
                    <GlobalHotKeys
                        keyMap={{
                            close: ['esc'],
                        }}
                        handlers={{
                            close: event => {
                                handleCancel(event)
                            },
                        }}
                        allowChanges={true}
                    /> : null
            }
        </>
        <form onSubmit={handleSubmit}>
            <fieldset disabled={examInputs.isSaving}>
                <div className='quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('exam-input', 'default')}
                        action='Update'
                        category='Exam Inputs'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        <FadeIn>
                            <div className='update-exam-input-multiple body-content'>
                                <section>
                                    {
                                        tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_SIZE) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='update-exam-input-size'><small>Size</small></label>
                                                        <select
                                                            id='update-exam-input-size'
                                                            className='custom-select form-control'
                                                            value={size ? size : ''}
                                                            onChange={handleSizeChange}
                                                        >
                                                            <option value='sm'>Small</option>
                                                            <option value=''>Standard</option>
                                                            <option value='lg'>Large</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_ALIGN) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='update-exam-input-align'><small>Align</small></label>
                                                        <div>
                                                            <div className='btn-group'>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-secondary' + (align === 'left' ? ' active' : '')}
                                                                    value={(align ? align : '')}
                                                                    onClick={() => { handleAlignChange('left') }}
                                                                >
                                                                    <i className='fal fa-align-left'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-secondary' + (align === 'center' ? ' active' : '')}
                                                                    value={(align ? align : '')}
                                                                    onClick={() => { handleAlignChange('center') }}
                                                                >
                                                                    <i className='fal fa-align-center'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-secondary' + (align === 'right' ? ' active' : '')}
                                                                    value={(align ? align : '')}
                                                                    onClick={() => { handleAlignChange('right') }}
                                                                >
                                                                    <i className='fal fa-align-right'></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_VALIGN) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='update-exam-input-valign'><small>Vertical Align</small></label>
                                                        <div>
                                                            <div className='btn-group'>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-secondary' + (valign === 'top' ? ' active' : '')}
                                                                    value={(valign ? valign : '')}
                                                                    onClick={() => { handleValignChange('top') }}
                                                                >
                                                                    <i className='fal fa-arrow-to-top'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-secondary' + (valign === 'middle' ? ' active' : '')}
                                                                    value={(valign ? valign : '')}
                                                                    onClick={() => { handleValignChange('middle') }}
                                                                >
                                                                    <i className='fal fa-horizontal-rule'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-secondary' + (valign === 'bottom' ? ' active' : '')}
                                                                    value={(valign ? valign : '')}
                                                                    onClick={() => { handleValignChange('bottom') }}
                                                                >
                                                                    <i className='fal fa-arrow-to-bottom'></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_INLINE) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label><small>Inline</small></label>
                                                        <div>
                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                <input
                                                                    id={`update-exam-input-inline-true`}
                                                                    type='radio'
                                                                    name='exam-input-inline'
                                                                    className='custom-control-input'
                                                                    value={true}
                                                                    checked={inline}
                                                                    onChange={handleInlineChange}
                                                                />
                                                                <label
                                                                    htmlFor={`update-exam-input-inline-true`}
                                                                    className='custom-control-label'
                                                                >
                                                                    Yes
                                                                </label>
                                                            </div>
                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                <input
                                                                    id={`update-exam-input-inline-false`}
                                                                    type='radio'
                                                                    name='exam-input-inline'
                                                                    className='custom-control-input'
                                                                    value={false}
                                                                    checked={!inline}
                                                                    onChange={handleInlineChange}
                                                                />
                                                                <label
                                                                    htmlFor={`update-exam-input-inline-false`}
                                                                    className='custom-control-label'
                                                                >
                                                                    No
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_DATA_TYPE) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-exam-input-data-type'><small>Data Type</small></label>
                                                        <select
                                                            id='update-exam-input-data-type'
                                                            className='custom-select form-control'
                                                            value={dataType ? dataType : ''}
                                                            onChange={handleDataTypeChange}
                                                        >
                                                            <option value=''>(Not set)</option>
                                                            {
                                                                tih.getDataTypes().map((dt, dti) => {
                                                                    return <option
                                                                        key={`update-exam-input-data-type_${dti}`}
                                                                        value={dt}
                                                                    >{dt}</option>
                                                                })
                                                            }
                                                        </select>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_TYPE) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_TYPE) ?
                                            <div className='row'>
                                                <div className='col-4'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-exam-input-padding-type'><small>Data Padding</small></label>
                                                        <select
                                                            id='update-exam-input-padding-type'
                                                            className='custom-select form-control'
                                                            value={paddingType ? paddingType : ''}
                                                            onChange={handlePaddingTypeChange}
                                                        >
                                                            <option value=''>(None)</option>
                                                            <option value='left'>Left</option>
                                                            <option value='right'>Right</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className='col-4'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-exam-input-padding-char'><small>Character</small></label>
                                                        <input
                                                            id='update-exam-input-padding-char'
                                                            type='text'
                                                            maxLength={1}
                                                            className='form-control'
                                                            autoComplete='off'
                                                            value={paddingCharacter ? paddingCharacter : ''}
                                                            disabled={!paddingType}
                                                            onChange={handlePaddingCharacterChange}
                                                            onBlur={handlePaddingBlur}
                                                        />
                                                    </div>
                                                </div>
                                                <div className='col-4'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-exam-input-padding-length'><small>Length</small></label>
                                                        <input
                                                            id='update-exam-input-padding-length'
                                                            type='number'
                                                            min={2}
                                                            max={10}
                                                            step={1}
                                                            className='form-control'
                                                            autoComplete='off'
                                                            disabled={!paddingCharacter || paddingCharacter.length === 0}
                                                            value={paddingLength ? paddingLength : ''}
                                                            onChange={handlePaddingLengthChange}
                                                            onBlur={handlePaddingBlur}
                                                        />
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_DATA_DEFAULT_NEGATIVE) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_DEFAULT_NEGATIVE) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <div className='custom-control custom-switch'>
                                                            <input
                                                                id='update-exam-input-default-negative'
                                                                type='checkbox'
                                                                name='update-exam-input-default-negative'
                                                                className='custom-control-input'
                                                                checked={isDefaultNegative}
                                                                onChange={handleDefaultNegativeChange}
                                                            />
                                                            <label
                                                                htmlFor='update-exam-input-default-negative'
                                                                className='custom-control-label'
                                                            >
                                                                Default as negative number
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_DATA_DECIMAL_PLACES) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_DECIMAL_PLACES) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-exam-input-decimal-places'><small>Decimal Places</small></label>
                                                        <input
                                                            id='update-exam-input-decimal-places'
                                                            type='number'
                                                            min={1}
                                                            max={5}
                                                            step={1}
                                                            className='form-control'
                                                            autoComplete='off'
                                                            value={decimalPlaces ? decimalPlaces : ''}
                                                            onChange={handleDecimalPlacesChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-exam-input-format'><small>Format</small></label>
                                                        {
                                                            tih.getFormatOptions(dataType, decimalPlaces).map((o, oi) => {
                                                                return <div
                                                                    key={`update-exam-input-format-${oi}`}
                                                                    className='custom-control custom-radio mb-2'
                                                                >
                                                                    <input
                                                                        id={`update-exam-input-format-${oi}`}
                                                                        type='radio'
                                                                        name='updateExamInputFormat'
                                                                        value={o.type}
                                                                        className='custom-control-input'
                                                                        checked={format === o.type}
                                                                        onChange={handleFormatChange}
                                                                    />
                                                                    <label
                                                                        htmlFor={`update-exam-input-format-${oi}`}
                                                                        className='custom-control-label'
                                                                    >
                                                                        {o.example}
                                                                    </label>
                                                                </div>
                                                            })
                                                        }
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT_SEARCH) && tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT_REPLACE) ?
                                            <div className='row'>
                                                <div className='col-6'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-exam-input-format-search'><small>Search <span className='tt-normal fw-400'>(Regular Expression)</span></small></label>
                                                        <select
                                                            id='update-exam-input-format-search'
                                                            className='form-control'
                                                            value={formatSearch ? formatSearch : ''}
                                                            onChange={handleFormatSearchChange}
                                                        >
                                                            <option></option>
                                                            <option value={'^0+$'}>Axis ZERO</option>
                                                            <option value={'^[+-]?0(\\.0+)?$'}>Number ZERO</option>
                                                            <option value={'^(\\d+)([+-]\\d*)?$'}>Visual Acuity number</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className='col-6'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-exam-input-format-replace'><small>Replace <span className='tt-normal fw-400'>(Regular Expression)</span></small></label>
                                                        <select
                                                            id='update-exam-input-format-replace'
                                                            className='form-control'
                                                            value={formatReplace ? formatReplace : ''}
                                                            onChange={handleFormatReplaceChange}
                                                        >
                                                            <option></option>
                                                            <option value={'Plano'}>Plano</option>
                                                            <option value={'PL'}>PL</option>
                                                            <option value={'SPH'}>SPH</option>
                                                            <option value={'180'}>Axis (0 = 180)</option>
                                                            <option value={'20/$1$2'}>Visual Acuity</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_DATA_VALIDATION) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-exam-input-validation-regex'><small>Validation <span className='tt-normal fw-400'>(Regular Expression)</span></small></label>
                                                        <input
                                                            id='update-exam-input-validation-regex'
                                                            type='text'
                                                            maxLength={255}
                                                            className='form-control'
                                                            autoComplete='off'
                                                            value={validation ? validation : ''}
                                                            onChange={handleValidationChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_DATA_STEP) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_STEP) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='update-exam-input-step'><small>Step</small></label>
                                                        <input
                                                            id='update-exam-input-step'
                                                            type='text'
                                                            maxLength={255}
                                                            className='form-control'
                                                            autoComplete='off'
                                                            value={step ? step : ''}
                                                            onChange={handleStepChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tih.hasField(examInputs.type, tih.TEMPLATE_INPUT_METADATA_HISTORY_TOOLTIP) ?
                                            <>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group'>
                                                            <label htmlFor='update-exam-input-history-tooltip'><small>History Tooltip</small></label>
                                                            <div className='custom-control custom-checkbox mb-2'>
                                                                <input
                                                                    id='update-exam-input-history-tooltip'
                                                                    type='checkbox'
                                                                    name='update-exam-input-history-tooltip'
                                                                    className='custom-control-input'
                                                                    checked={historyTooltipEnabled}
                                                                    onChange={handleHistoryTooltipEnabledChange}
                                                                />
                                                                <label
                                                                    htmlFor='update-exam-input-history-tooltip'
                                                                    className='custom-control-label'
                                                                >
                                                                    Enable History Tooltip
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </> : null
                                    }
                                </section>
                            </div>
                        </FadeIn>
                    </div>
                    <div className='quick-drawer-action'>
                        <div className='row'>
                            <div className='col-12'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-link btn-cancel mr-2'
                                        onClick={handleCancel}
                                    >Cancel</button>
                                    <button
                                        type='submit'
                                        className='btn btn-success'
                                    >Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
    </>)
}

export default UpdateExamInputMultiple;